// Central content for VectorBridge Group LLC

export const NAV = [
  { label: "About", to: "/about" },
  { label: "Government", to: "/government" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

// Institutional philosophy statement (Why VectorBridge Exists)
export const PHILOSOPHY =
  "We believe workforce quality is a strategic advantage. Every organization depends on people capable of advancing its mission. Our responsibility is to identify, evaluate, and deliver professionals whose performance strengthens the organizations they serve. We measure success not by placements made, but by missions advanced.";

// Trust signals / institutional credentials.
// status: "active" (true today) or "pending" (in progress / coming soon).
// Flip a status to "active" once the registration is complete.
export const TRUST_SIGNALS = [
  { label: "SAM.gov Registered", icon: "ShieldCheck", status: "active" },
  { label: "Virginia SWaM", icon: "BadgeCheck", status: "active", statusLabel: "Certified Small · Micro · Minority-Owned" },
  { label: "Veteran-Owned", icon: "Medal", status: "active" },
  { label: "Nationwide Service", icon: "Globe", status: "active", statusLabel: "50 States" },
];

// Government contracting leads the list of capabilities.
export const SERVICES = [
  { id: "01", title: "Government Contractor Recruiting", desc: "Cleared and clearable talent aligned to federal contract requirements, agency compliance, and program timelines." },
  { id: "02", title: "Proposal Staffing Support", desc: "Contingent hire commitments, letters of intent, and named key personnel to strengthen competitive bids." },
  { id: "03", title: "Managed Workforce Services", desc: "Program-managed contingent workforces with governance, reporting, and SLAs built for accountability." },
  { id: "04", title: "Direct Hire Recruiting", desc: "Permanent placement of vetted professionals across technical, clinical, and administrative functions." },
  { id: "05", title: "Executive Search", desc: "Retained search for senior leadership, C-suite, and mission-critical directorate roles." },
  { id: "06", title: "Recruitment Process Outsourcing", desc: "End-to-end RPO that operates as an extension of your talent acquisition function." },
  { id: "07", title: "Temporary Staffing", desc: "Scalable contingent talent to meet surge demand, seasonal cycles, and project timelines." },
  { id: "08", title: "Temp-to-Hire", desc: "Evaluate performance and cultural fit before extending a permanent offer." },
  { id: "09", title: "Workforce Planning", desc: "Data-driven capacity modeling to align headcount with strategic objectives." },
  { id: "10", title: "Human Capital Consulting", desc: "Advisory across talent strategy, retention, and workforce transformation." },
  { id: "11", title: "HR Advisory", desc: "Compliance, policy, and employee-relations guidance for scaling organizations." },
  { id: "12", title: "Organizational Design Support", desc: "Structure, role architecture, and operating models built for accountability." },
];

export const INDUSTRIES = [
  { name: "Government Agencies", slug: "government-agencies", tag: "Federal · State · Local", img: "https://images.unsplash.com/photo-1688417486337-b089fa4ee670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxnb3Zlcm5tZW50JTIwZGVmZW5zZSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg0NjQ5ODUzfDA&ixlib=rb-4.1.0&q=85" },
  { name: "Defense Contractors", slug: "defense-contractors", tag: "Cleared Talent · GovCon", img: "https://images.pexels.com/photos/28823758/pexels-photo-28823758.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Healthcare Organizations", slug: "healthcare-organizations", tag: "Clinical · Allied · Admin", img: "https://images.pexels.com/photos/7433840/pexels-photo-7433840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Professional Services", slug: "professional-services", tag: "Finance · Legal · Tech", img: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGZhY2FkZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODQ2NDk4NTN8MA&ixlib=rb-4.1.0&q=85" },
  { name: "Logistics Companies", slug: "logistics-companies", tag: "Supply Chain · Operations", img: "https://images.pexels.com/photos/26547201/pexels-photo-26547201.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Commercial Enterprises", slug: "commercial-enterprises", tag: "Growth · Scale · Transform", img: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGZhY2FkZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODQ2NDk4NTN8MA&ixlib=rb-4.1.0&q=85" },
];

export const SERVICE_GROUPS = [
  {
    label: "Government Contracting",
    items: [
      { id: "01", title: "Government Contractor Recruiting", slug: "government-contractor-recruiting" },
      { id: "02", title: "Proposal Staffing Support", slug: "proposal-staffing-support" },
      { id: "03", title: "Managed Workforce Services", slug: "managed-workforce-services" },
    ],
  },
  {
    label: "Talent Acquisition",
    items: [
      { id: "04", title: "Direct Hire Recruiting", slug: "direct-hire-recruiting" },
      { id: "05", title: "Executive Search", slug: "executive-search" },
      { id: "07", title: "Temporary Staffing", slug: "temporary-staffing" },
      { id: "08", title: "Temp-to-Hire", slug: "temp-to-hire" },
    ],
  },
  {
    label: "Advisory & Programs",
    items: [
      { id: "06", title: "Recruitment Process Outsourcing", slug: "recruitment-process-outsourcing" },
      { id: "09", title: "Workforce Planning", slug: "workforce-planning" },
      { id: "10", title: "Human Capital Consulting", slug: "human-capital-consulting" },
      { id: "11", title: "HR Advisory", slug: "hr-advisory" },
      { id: "12", title: "Organizational Design Support", slug: "organizational-design-support" },
    ],
  },
];

export const MANIFESTO = [
  { n: "01", title: "Precision", body: "Every placement is a deliberate match of capability, character, and mission. We measure twice and commit once." },
  { n: "02", title: "Integrity", body: "We operate with transparency and hold ourselves to the compliance standards our government and defense clients require." },
  { n: "03", title: "Accountability", body: "We stand behind every candidate and every contract with clear SLAs, documented process, and measurable outcomes." },
  { n: "04", title: "Stewardship", body: "We treat client budgets and candidate careers as responsibilities, not transactions." },
];

export const STATS = [
  { k: "50", label: "States Served" },
  { k: "12", label: "Workforce Capabilities" },
  { k: "6", label: "Core Sectors" },
  { k: "100%", label: "Compliance Focused" },
];

export const CONTACT = {
  email: "admin@vectorbridgegroup.com",
  careersEmail: "careers@vectorbridgegroup.com",
  adminEmail: "admin@vectorbridgegroup.com",
  phone: "+1 (757) 394-1014",
  address: "6550 Hampton Roads Pkwy, Ste 103B, Suffolk, VA 23435-2763",
  uei: "KR62WHB4DM83",
  cage: "24LW2",
};
